import { debounce } from 'lodash-es';
import { useEffect, useMemo, useRef } from 'react';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { useCatalogDataContextRefreshEventForItemsSelector } from 'src/contexts/CatalogDataContext';
import { useListingNotificationContext } from 'src/contexts/ListingNotificationContext';
import { entityTypeNumberToEntityTypeEnum } from 'src/utils/activityLogUtils';
import { ActionOutboxEntityType, Listing } from 'src/WebApiController';

const NOTIFICATION_DEBOUNCE_MS = 3000;

export const useRefreshListingOnNotification = () => {
  const { hubConnection } = useListingNotificationContext();

  const refreshEventForItems =
    useCatalogDataContextRefreshEventForItemsSelector();
  const { setActivePosEntity, posEntityId, posEntity } =
    useActivePosEntityContext<Listing>();

  // Keep latest callback identities in refs so the debounced functions below
  // can be created once and never lose their pending timer when callers
  // re-render.
  const latestRefreshEventForItems = useRef(refreshEventForItems);
  latestRefreshEventForItems.current = refreshEventForItems;
  const latestSetActivePosEntity = useRef(setActivePosEntity);
  latestSetActivePosEntity.current = setActivePosEntity;

  const pendingListingIdsToRefresh = useRef<Set<number>>(new Set());

  const debouncedRefreshPendingListings = useMemo(
    () =>
      debounce(() => {
        if (pendingListingIdsToRefresh.current.size === 0) return;
        const listingIds = Array.from(pendingListingIdsToRefresh.current);
        pendingListingIdsToRefresh.current.clear();
        latestRefreshEventForItems.current(
          listingIds,
          ActionOutboxEntityType.Listing
        );
      }, NOTIFICATION_DEBOUNCE_MS),
    []
  );

  const debouncedRefreshActivePosEntity = useMemo(
    () =>
      debounce((listingId: number) => {
        latestSetActivePosEntity.current(listingId, undefined, true);
      }, NOTIFICATION_DEBOUNCE_MS),
    []
  );

  useEffect(() => {
    return () => {
      debouncedRefreshPendingListings.cancel();
      debouncedRefreshActivePosEntity.cancel();
    };
  }, [debouncedRefreshPendingListings, debouncedRefreshActivePosEntity]);

  useEffect(() => {
    const handleListingNotification = async (rawMessage: string) => {
      try {
        const notifications = JSON.parse(rawMessage);
        for (const notification of notifications) {
          console.debug(
            'useRefreshListingOnNotification - got new notification',
            notification,
            hubConnection
          );

          // Refresh the currently-open listing detail view.
          if (notification.EntityId === posEntityId && posEntityId) {
            debouncedRefreshActivePosEntity(posEntityId);
          }

          // Batch listing-level updates and flush them as one refresh.
          const entityType = entityTypeNumberToEntityTypeEnum(
            notification.EntityType
          );
          if (
            entityType === ActionOutboxEntityType.Listing &&
            notification.EntityId
          ) {
            pendingListingIdsToRefresh.current.add(notification.EntityId);
            debouncedRefreshPendingListings();
          }
        }
      } catch (e) {
        console.error(
          'useRefreshListingOnNotification - malformed SignalR Message: ' +
            rawMessage
        );
      }
    };

    if (hubConnection) {
      hubConnection.on('new', handleListingNotification);
    }

    return () => {
      if (hubConnection) {
        hubConnection.off('new', handleListingNotification);
      }
    };
  }, [
    debouncedRefreshActivePosEntity,
    debouncedRefreshPendingListings,
    hubConnection,
    posEntity?.rowVer,
    posEntityId,
  ]);
};
